import { useContext, type MouseEvent, type ReactNode } from 'react';
import { MooChatContext } from '../../../provider/internal-context';

/** True when the href points at another site, such as a Google Doc the agent
 *  just uploaded. Relative and same-origin links stay in the console tab. */
export const isExternalHref = (href: string | undefined): boolean => {
  if (!href) return false;
  try {
    const url = new URL(href, globalThis.location.href);
    return (
      (url.protocol === 'http:' || url.protocol === 'https:') &&
      url.origin !== globalThis.location.origin
    );
  } catch {
    return false;
  }
};

/** The in-app route for a same-origin page link (such as a new quote's review
 *  page), or null for anything the browser should handle itself: another
 *  site, a non-web scheme, or an in-page anchor. */
export const inAppRoute = (href: string | undefined): string | null => {
  if (!href || href.startsWith('#')) return null;
  try {
    const url = new URL(href, globalThis.location.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (url.origin !== globalThis.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

/** A plain left click, which the router takes over. Modified and middle clicks
 *  keep their browser meaning (new tab, new window). */
const isPlainClick = (event: MouseEvent<HTMLAnchorElement>): boolean =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

/** Opens external links in a new tab so following one does not navigate the
 *  console away from the conversation. In-app links go through the host's
 *  router rather than reloading the console. Only the props a link needs are
 *  forwarded, which drops the `node` prop react-markdown also passes. */
export function MarkdownLink({
  href,
  title,
  children,
}: Readonly<{ href?: string; title?: string; children?: ReactNode }>) {
  const navigation = useContext(MooChatContext)?.config.navigation;
  if (isExternalHref(href)) {
    return (
      <a href={href} title={title} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  const route = inAppRoute(href);
  const handleClick =
    navigation && route
      ? (event: MouseEvent<HTMLAnchorElement>) => {
          if (!isPlainClick(event)) return;
          event.preventDefault();
          navigation.navigate(route);
        }
      : undefined;
  return (
    <a href={href} title={title} onClick={handleClick}>
      {children}
    </a>
  );
}
